<template>
    <q-page class="public-view form">
        <!-- reset success form (activated) -->
        <q-card-section v-if="isReset">
            <h1 class="activated-message">
                <q-icon
                    name="fal fa-check-circle"
                    class="q-ma-sm"
                    size="md"
                    color="green"
                ></q-icon>
                {{ $t('Your password was successfully updated') }}!
            </h1>
            <!-- Form Body-->
            <q-form
                :class="$q.platform.is.ipad ? 'ios-form' : 'q-pt-md q-px-md'"
                v-if="isReset"
            >
                <q-btn
                    class="q-mt-md"
                    @click="$router.push({ name: 'login' })"
                    :label="$t('Return to login')"
                    style="width: 100%"
                    text-color="primary"
                    color="white"
                    outline
                />
            </q-form>
        </q-card-section>

        <!-- reset form  (deactivated) -->
        <q-card-section v-else>
            <q-banner
                id="error-message"
                v-for="(errorMsg, index) in errorMessages"
                :key="`error-${index}`"
                class="background-red border-red q-mb-md q-mr-sm"
                rounded
                inline-actions
            >
                <template v-slot:avatar>
                    <q-icon
                        name="fa-light fa-circle-exclamation"
                        color="negative"
                        class="q-pt-sm"
                        size="sm"
                    />
                </template>
                <span class="text-negative">{{ $t(errorMsg) }}</span>
            </q-banner>
            <p class="reset-message">
                {{ $t('Enter your new password.') }}
            </p>
            <!-- Form Body -->
            <q-form
                @submit="handleSubmit"
                :class="$q.platform.is.ipad ? 'ios-form' : 'q-pt-md q-px-md'"
            >
                <password-and-confirmation @inputUpdate="updatePassword" />
                <q-btn
                    id="submit-button"
                    :label="$t('Submit')"
                    style="width: 100%"
                    type="submit"
                    class="q-my-md"
                    color="primary"
                    :disabled="disableSubmit"
                />
            </q-form>
        </q-card-section>
    </q-page>
</template>

<script>
import PasswordAndConfirmation from '@components/PasswordAndConfirmation.vue';
import { mapActions } from 'pinia';
import { useSharedStore } from '@stores/shared.js';
import { useAuthStore } from '@stores/auth';

export default {
    name: 'ResetPasswordConfirm',
    components: { PasswordAndConfirmation },
    data() {
        return {
            form: {
                newPassword: '',
                confirmedPassword: ''
            },
            uid: null,
            token: null,
            showPassword: false,
            isReset: false,
            disableSubmit: true,
            // Array of error messages to be displayed in the banner
            errorMessages: []
        };
    },
    computed: {
        /**
         * Server formatted payload.
         * @return {Object} - Server formatted payload of the form.
         */
        payload() {
            // prepare the payload
            const payload = {
                uid: this.uid,
                token: this.token,
                new_password: this.form.newPassword
            };
            return payload;
        }
    },
    async created() {
        // set the uid and token from route
        this.uid = this.$route.params['uid'];
        this.token = this.$route.params['token'];

        // show the reset password message if the query is present
        if (this.$route.query['showResetPasswordMessage']) {
            this.errorMessages.push(
                this.$t(
                    'Your account requires a password reset before you can log in. Please reset your password to continue.'
                )
            );
        }
    },
    methods: {
        ...mapActions(useSharedStore, ['beginLoading', 'stopLoading']),
        ...mapActions(useAuthStore, ['getCsrfTokenAction']),
        /**
         * Handles activation submission.
         */
        async handleSubmit() {
            // begin loading
            this.beginLoading(`${this.$t('Sending request')}...`);

            // set CSRF token in header
            this.$axios.defaults.headers.common['X-CSRFToken'] =
                await this.getCsrfTokenAction();

            try {
                const response = await this.$axios.post(
                    'users/reset_password_confirm/',
                    this.payload,
                    {
                        disableRequestInterceptor: true
                    }
                );
                // if status code is 204, the password is successfully updated
                if (response.status === 204) {
                    this.$notifySuccess(
                        this.$t('Your password was successfully updated')
                    );
                    this.isReset = true;
                } else {
                    // notify other issues
                    this.$notifyError(
                        this.$t(
                            'Unable to reset your password. Please try again later.'
                        )
                    );
                }
            } catch (e) {
                const error =
                    e?.response?.data && typeof e.response.data === 'object'
                        ? e.response.data
                        : {};
                // display the password issues on the banner
                if (error['new_password']) {
                    // Clear the error messages array
                    this.errorMessages = [];
                    error['new_password'].forEach(errorMsg => {
                        // Push the error message to the array
                        this.errorMessages.push(errorMsg);
                    });
                    this.$notifyError(
                        this.$t(
                            'Please review the errors below and try to set your password again.'
                        ),
                        error
                    );
                } else if (error['uid']) {
                    // notify uid issues
                    error['uid'].forEach(errorMsg => {
                        this.$notifyError(errorMsg, error);
                    });
                } else if (error['token']) {
                    // notify token issues
                    error['token'].forEach(errorMsg => {
                        this.$notifyError(errorMsg, error);
                    });
                } else if (error.code && error.code === 'password_no_reuse') {
                    // notify password reuse issues
                    this.$notifyError(
                        this.$t(
                            "Your new password can't be the same as your current one. Please choose a different password."
                        )
                    );
                } else {
                    this.$notifyError(
                        this.$t(
                            'Unable to reset your password. Please try again later.'
                        ),
                        error
                    );
                }
            } finally {
                // stop loading
                this.stopLoading();
            }
        },
        updatePassword(passwordObj) {
            this.form.newPassword = passwordObj.new;
            this.form.confirmedPassword = passwordObj.confirmation;
            this.disableSubmit = !passwordObj.isConfirmed;
        }
    }
};
</script>

<style lang="scss" scoped>
@import '@css/auth_forms.scss';
.form {
    width: 400px;
}

.reset-message {
    font-size: 16px;
    margin-top: 30px;
    margin-bottom: 20px;
    text-align: center;
}
.activated-message {
    font-size: 16px;
    margin-top: 50px;
    text-align: center;
}
</style>
